import React, { useEffect, useMemo, useState } from "react";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal/Modal";

import {
  FaCalendarAlt,
  FaFlag,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaPlus,
  FaSave,
  FaTrash,
  FaUsers,
  FaTimes,
} from "react-icons/fa";

import {
  AGE_LABELS,
  DISCIPLINE_OPTIONS,
  EMPTY_EVENT,
  EVENT_LEVEL_OPTIONS,
  EVENT_TYPE_OPTIONS,
} from "./data/eventOptions";

import {
  formatDateFR,
  formatDateInput,
  parseEventCategories,
  toBoolean,
  toCanonicalAge,
} from "./utils/eventUtils";

const inputClass =
  "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white";

function FieldLabel({ children, required = false }) {
  return (
    <label className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
      {children}
      {required ? <span className="ml-1 text-red-500">*</span> : null}
    </label>
  );
}

export default function EventFormModal({
  isOpen,
  onClose,
  initialData,
  onSubmit,
}) {
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [imageIsValid, setImageIsValid] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    const data = initialData || EMPTY_EVENT;

    setForm({
      ...EMPTY_EVENT,
      ...data,
      titre: data?.titre ?? "",
      description: data?.description ?? "",
      discipline_event: data?.discipline_event ?? "",
      type_event: data?.type_event ?? "",
      level_event: data?.level_event ?? "",
      cate_event: data?.cate_event ?? "",
      lieu_event: data?.lieu_event ?? "",
      horaire_event: data?.horaire_event ?? "",
      photo_url: data?.photo_url ?? "",
      agenda_url: data?.agenda_url ?? "",
      suivi_event: data?.suivi_event ?? "",
      organisateur: data?.organisateur ?? "",
      start: formatDateInput(data?.start),
      end: formatDateInput(data?.end),
      is_active: toBoolean(data?.is_active, true),
    });

    setCategories(parseEventCategories(data?.cate_event));
    setSelectedCategory("");
    setImageIsValid(true);
    setSaving(false);
  }, [initialData, isOpen]);

  const disciplineOptions = useMemo(() => {
    const currentDiscipline = String(
      form.discipline_event || ""
    ).trim();

    if (
      currentDiscipline &&
      !DISCIPLINE_OPTIONS.includes(currentDiscipline)
    ) {
      return [currentDiscipline, ...DISCIPLINE_OPTIONS];
    }

    return DISCIPLINE_OPTIONS;
  }, [form.discipline_event]);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;

    setForm((previousForm) => ({
      ...previousForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addCategory = () => {
    if (!selectedCategory) return;

    const normalizedCategory =
      toCanonicalAge(selectedCategory) || selectedCategory;

    setCategories((previousCategories) => {
      const alreadyExists = previousCategories.some(
        (category) => category === normalizedCategory
      );

      if (alreadyExists) {
        return previousCategories;
      }

      return parseEventCategories([
        ...previousCategories,
        normalizedCategory,
      ]);
    });

    setSelectedCategory("");
  };

  const removeCategory = (categoryToRemove) => {
    setCategories((previousCategories) =>
      previousCategories.filter(
        (category) => category !== categoryToRemove
      )
    );
  };

  const clearCategories = () => {
    setCategories([]);
  };

  const submit = async (event) => {
    event.preventDefault();

    const title = form.titre?.trim();

    if (!title || !form.start) {
      return;
    }

    if (
      form.end &&
      new Date(form.end).getTime() <
        new Date(form.start).getTime()
    ) {
      window.alert(
        "La date de fin ne peut pas être antérieure à la date de début."
      );

      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        titre: title,
        description: form.description?.trim() || null,
        discipline_event:
          form.discipline_event?.trim() || null,
        type_event: form.type_event?.trim() || null,
        level_event: form.level_event?.trim() || null,
        cate_event:
          categories.length > 0
            ? categories.join(", ")
            : null,
        lieu_event: form.lieu_event?.trim() || null,
        horaire_event:
          form.horaire_event?.trim() || null,
        photo_url: form.photo_url?.trim() || null,
        agenda_url: form.agenda_url?.trim() || null,
        suivi_event:
          form.suivi_event?.trim() || null,
        organisateur:
          form.organisateur?.trim() || null,
        start: form.start || null,
        end: form.end || null,
        is_active: Boolean(form.is_active),
      };

      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isEditing = Boolean(initialData?.id);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById="event-form-title"
    >
      <ModalHeader
        id="event-form-title"
        title={
          isEditing
            ? "Modifier un événement"
            : "Créer un événement"
        }
        subtitle="Renseignez les informations diffusées sur le calendrier de la Ligue"
        onClose={onClose}
      />

      <ModalBody>
        <div className="grid max-h-[72vh] gap-0 md:grid-cols-3">
          <div className="overflow-y-auto pr-1 md:col-span-2 md:pr-4">
            <form onSubmit={submit} className="space-y-5">
              <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                <h3 className="mb-4 text-sm font-bold text-slate-900 dark:text-white">
                  Informations principales
                </h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <FieldLabel required>Titre</FieldLabel>

                    <input
                      name="titre"
                      value={form.titre ?? ""}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder="Ex : Championnat régional cadets"
                    />
                  </div>

                  <div>
                    <FieldLabel>Organisateur</FieldLabel>

                    <input
                      name="organisateur"
                      value={form.organisateur ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Ex : Ligue Nouvelle-Aquitaine"
                    />
                  </div>

                  <div>
                    <FieldLabel required>Date de début</FieldLabel>

                    <input
                      type="date"
                      name="start"
                      value={form.start ?? ""}
                      onChange={handleChange}
                      required
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <FieldLabel>Date de fin</FieldLabel>

                    <input
                      type="date"
                      name="end"
                      value={form.end ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 text-sm font-bold text-slate-900 dark:text-white">
                  Classement de l’événement
                </h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <FieldLabel>Discipline</FieldLabel>

                    <select
                      name="discipline_event"
                      value={form.discipline_event ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">
                        Sélectionner une discipline
                      </option>

                      {disciplineOptions.map((discipline) => (
                        <option
                          key={discipline}
                          value={discipline}
                        >
                          {discipline}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <FieldLabel>Type d’événement</FieldLabel>

                    <select
                      name="type_event"
                      value={form.type_event ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">
                        Sélectionner un type
                      </option>

                      {EVENT_TYPE_OPTIONS.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <FieldLabel>Niveau</FieldLabel>

                    <select
                      name="level_event"
                      value={form.level_event ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                    >
                      <option value="">
                        Sélectionner un niveau
                      </option>

                      {EVENT_LEVEL_OPTIONS.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <FieldLabel>Lieu</FieldLabel>

                    <input
                      name="lieu_event"
                      value={form.lieu_event ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="Ville, dojo ou adresse"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <FieldLabel>Catégories d’âge</FieldLabel>

                    <div className="mt-1 flex items-center gap-2">
                      <select
                        value={selectedCategory}
                        onChange={(event) =>
                          setSelectedCategory(
                            event.target.value
                          )
                        }
                        className={`${inputClass} mt-0 flex-1`}
                      >
                        <option value="">
                          Sélectionner une catégorie
                        </option>

                        {AGE_LABELS.map((category) => (
                          <option
                            key={category}
                            value={category}
                          >
                            {category}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={addCategory}
                        disabled={!selectedCategory}
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Ajouter la catégorie"
                      >
                        <FaPlus />
                      </button>

                      {categories.length > 0 ? (
                        <button
                          type="button"
                          onClick={clearCategories}
                          className="h-11 shrink-0 rounded-xl border border-slate-300 px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                          Vider
                        </button>
                      ) : null}
                    </div>

                    {categories.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {categories.map((category) => (
                          <span
                            key={category}
                            className="inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-800 ring-1 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:ring-sky-800/50"
                          >
                            {category}

                            <button
                              type="button"
                              onClick={() =>
                                removeCategory(category)
                              }
                              className="opacity-70 transition hover:opacity-100"
                              aria-label={`Retirer ${category}`}
                              title={`Retirer ${category}`}
                            >
                              <FaTrash />
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        Aucune catégorie sélectionnée : l’événement
                        pourra être considéré comme ouvert à toutes
                        les catégories.
                      </p>
                    )}
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 text-sm font-bold text-slate-900 dark:text-white">
                  Informations pratiques
                </h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <FieldLabel>Horaires et informations</FieldLabel>

                    <textarea
                      name="horaire_event"
                      value={form.horaire_event ?? ""}
                      onChange={handleChange}
                      rows={3}
                      className={inputClass}
                      placeholder="Ex : Pesée à 8h30, début des combats à 9h30…"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <FieldLabel>Description</FieldLabel>

                    <textarea
                      name="description"
                      value={form.description ?? ""}
                      onChange={handleChange}
                      rows={5}
                      className={inputClass}
                      placeholder="Présentation complète de l’événement…"
                    />
                  </div>

                  <div>
                    <FieldLabel>Photo</FieldLabel>

                    <input
                      name="photo_url"
                      value={form.photo_url ?? ""}
                      onChange={(event) => {
                        setImageIsValid(true);
                        handleChange(event);
                      }}
                      className={inputClass}
                      placeholder="https://…"
                    />
                  </div>

                  <div>
                    <FieldLabel>Document PDF</FieldLabel>

                    <input
                      name="agenda_url"
                      value={form.agenda_url ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="https://…"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <FieldLabel>Suivi de compétition</FieldLabel>

                    <input
                      name="suivi_event"
                      value={form.suivi_event ?? ""}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="https://www.ffjudo.com/suivi-competition/…"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                      <div>
                        <div className="text-sm font-bold text-slate-900 dark:text-white">
                          Afficher sur le calendrier public
                        </div>

                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Un événement inactif reste disponible dans
                          l’administration.
                        </div>
                      </div>

                      <input
                        type="checkbox"
                        name="is_active"
                        checked={Boolean(form.is_active)}
                        onChange={handleChange}
                        className="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </label>
                  </div>
                </div>
              </section>

              <ModalFooter>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="inline-flex h-11 items-center gap-2 rounded-xl bg-slate-100 px-4 text-sm font-semibold text-slate-800 transition hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <FaTimes />
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={
                    saving ||
                    !form.titre?.trim() ||
                    !form.start
                  }
                  className="inline-flex h-11 items-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaSave />

                  {saving
                    ? "Enregistrement…"
                    : isEditing
                      ? "Enregistrer"
                      : "Créer l’événement"}
                </button>
              </ModalFooter>
            </form>
          </div>

          <aside className="hidden overflow-y-auto border-l border-slate-200 pl-4 dark:border-slate-800 md:col-span-1 md:block">
            <div className="sticky top-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Aperçu
                </div>
              </div>

              <div className="p-3">
                <img
                  src={
                    imageIsValid && form.photo_url
                      ? form.photo_url
                      : "/img/no-image.jpg"
                  }
                  alt={form.titre || "Aperçu de l'événement"}
                  className="block max-h-[34vh] w-full rounded-xl object-contain"
                  onError={() => setImageIsValid(false)}
                />
              </div>

              <div className="space-y-3 px-4 pb-4 text-sm text-slate-700 dark:text-slate-200">
                <h4 className="text-base font-bold text-slate-900 dark:text-white">
                  {form.titre || "Titre de l’événement"}
                </h4>

                {form.discipline_event ? (
                  <div className="flex items-start gap-2">
                    <FaLayerGroup className="mt-0.5 shrink-0 text-violet-500" />

                    <span>{form.discipline_event}</span>
                  </div>
                ) : null}

                {form.type_event ? (
                  <div className="flex items-start gap-2">
                    <FaLayerGroup className="mt-0.5 shrink-0 text-indigo-500" />

                    <span>{form.type_event}</span>
                  </div>
                ) : null}

                {form.level_event ? (
                  <div className="flex items-start gap-2">
                    <FaFlag className="mt-0.5 shrink-0 text-amber-500" />

                    <span>{form.level_event}</span>
                  </div>
                ) : null}

                {form.lieu_event ? (
                  <div className="flex items-start gap-2">
                    <FaMapMarkerAlt className="mt-0.5 shrink-0 text-red-500" />

                    <span>{form.lieu_event}</span>
                  </div>
                ) : null}

                {categories.length > 0 ? (
                  <div className="flex items-start gap-2">
                    <FaUsers className="mt-0.5 shrink-0 text-sky-500" />

                    <span>{categories.join(", ")}</span>
                  </div>
                ) : null}

                {form.start || form.end ? (
                  <div className="flex items-start gap-2">
                    <FaCalendarAlt className="mt-0.5 shrink-0 text-emerald-500" />

                    <span>
                      {formatDateFR(form.start)}

                      {form.end
                        ? ` → ${formatDateFR(form.end)}`
                        : ""}
                    </span>
                  </div>
                ) : null}

                {form.suivi_event ? (
                  <div className="pt-2">
                    <a
                      href={form.suivi_event}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex rounded-xl bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300"
                    >
                      Ouvrir le suivi compétition
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </ModalBody>
    </Modal>
  );
}